import { inject, injectable } from 'inversify'
import { Subscription } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { Route, Waypoint } from '@/domain/models'
import { Aircraft } from '@/domain/models/Aircraft'
import { Result } from '@/domain/protocols/Result'
import { FlightState } from '@/domain/states/FlightState'
import { RouteNavigationState } from '@/domain/states/RouteNavigationState'
import { RouteState } from '@/domain/states/RouteState'
import { SettingsState } from '@/domain/states/SettingsState'

import { IService } from '../IService'

import type { ILogger } from '@/infra/logger/ILogger'
import { isSet } from '@/utils/validation'
import { calculateMaximumCruiseAltitude, calculateMinimumCruiseAltitude, simulateWaypointsInfo } from '@/utils/vertical'

@injectable()
export class CruiseAltitudeSuggester implements IService {
  private changeRouteSubject: Subscription
  private changeFlightSubject: Subscription
  private restartByRouteSubject: Subscription
  private restartByFlightSubject: Subscription
  private changeSettingsSubject: Subscription
  private currentAircraftId: string = null
  private currentOperationalCeiling: number = null
  private currentCruiseAltitude: number = null
  private currentRouteId: string = null
  private currentDeparture: Waypoint = null
  private currentDestination: Waypoint = null

  constructor(
    @inject(InjectionTokens.RouteState) private routeState: RouteState,
    @inject(InjectionTokens.RouteNavigationState) private routeNavigationState: RouteNavigationState,
    @inject(InjectionTokens.SettingsState) private settingsState: SettingsState,
    @inject(InjectionTokens.FlightState) private flightState: FlightState,
    @inject(InjectionTokens.Logger) private logger: ILogger
  ) {}

  async init(): Promise<Result<void>> {
    const route = this.routeState?.getStateSnapshot()?.activeRoute
    this.currentRouteId = route?.id?.toString() ?? null
    this.currentDeparture = route?.waypoints[0] ?? null
    this.currentDestination = route?.waypoints[route.waypoints.length - 1] ?? null
    this.currentAircraftId = this.flightState?.getStateSnapshot()?.activeAircraft?.id?.toString() ?? null
    this.currentOperationalCeiling = this.flightState?.getStateSnapshot()?.activeAircraft?.operationalCeiling ?? null
    this.currentCruiseAltitude = this.flightState?.getStateSnapshot()?.activeCruiseAltitude ?? null

    this.changeSettingsSubject = this.settingsState.getState().subscribe({
      next: (settingsState) => {
        if (settingsState.cruiseAltitudeSuggesterStatus === 'running') {
          this.start()
        } else if (settingsState.cruiseAltitudeSuggesterStatus === 'stopped') {
          this.flightState.setCruiseAltitudeSuggested(false)
          this.stop()
        }
      }
    })

    return Result.ok()
  }
  start(): void | Promise<void> | Result<void> | Promise<Result<void>> {
    if (this.restartByRouteSubject) {
      this.restartByRouteSubject.unsubscribe()
      this.restartByRouteSubject = null
    }
    if (this.restartByFlightSubject) {
      this.restartByFlightSubject.unsubscribe()
      this.restartByFlightSubject = null
    }

    this.suggestAltitude()

    this.changeRouteSubject = this.routeState.getState().subscribe({
      next: (routeState) => {
        if (this.currentRouteId?.toString() !== routeState.activeRoute?.id?.toString()) {
          this.currentRouteId = routeState.activeRoute?.id?.toString()
          this.currentDeparture = routeState.activeRoute?.waypoints[0]
          this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          this.suggestAltitude(null, routeState.activeRoute)
        } else if (
          this.currentDeparture?.id?.toString() ===
            routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]?.id?.toString() &&
          this.currentDestination?.id?.toString() === routeState.activeRoute?.waypoints[0]?.id?.toString()
        ) {
          this.currentRouteId = routeState.activeRoute?.id?.toString()
          this.currentDeparture = routeState.activeRoute?.waypoints[0]
          this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          this.suggestAltitude(null, routeState.activeRoute)
        } else if (this.currentDeparture?.id?.toString() !== routeState.activeRoute?.waypoints[0]?.id?.toString()) {
          this.currentDeparture = routeState.activeRoute?.waypoints[0]
          this.suggestAltitude(null, routeState.activeRoute)
        } else if (
          this.currentDestination?.id?.toString() !==
          routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]?.id?.toString()
        ) {
          this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          this.suggestAltitude(null, routeState.activeRoute)
        } else {
          // verificando a mudança nos atributos da classe e atualizando, se necessário

          // currentRouteId
          if (this.currentRouteId?.toString() !== routeState.activeRoute?.id?.toString()) {
            this.currentRouteId = routeState.activeRoute?.id.toString()
          }
          // currentDeparture
          if (this.currentDeparture?.id?.toString() !== routeState.activeRoute?.waypoints[0]?.id?.toString()) {
            this.currentDeparture = routeState.activeRoute?.waypoints[0]
          }
          // currentDestination
          if (
            this.currentDestination?.id?.toString() !==
            routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]?.id?.toString()
          ) {
            this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          }
        }
      }
    })

    this.changeFlightSubject = this.flightState.getState().subscribe({
      next: (flightState) => {
        if (!flightState.activeAircraft) {
          this.currentAircraftId = null
          this.currentOperationalCeiling = null
          this.currentCruiseAltitude = null
          return
        }

        if (!flightState.activeAircraft && flightState.activeCruiseAltitude) {
          this.currentAircraftId = null
          this.currentOperationalCeiling = null
          this.currentCruiseAltitude = null
          this.flightState.updateState({
            activeCruiseAltitude: null,
            isCruiseAltitudeSuggested: false
          })
        } else if (this.currentAircraftId?.toString() !== flightState.activeAircraft?.id?.toString()) {
          this.currentAircraftId = flightState.activeAircraft?.id?.toString()
          this.currentOperationalCeiling = flightState.activeAircraft?.operationalCeiling
          this.suggestAltitude(flightState.activeAircraft)
        } else if (this.currentOperationalCeiling !== flightState.activeAircraft?.operationalCeiling) {
          this.currentOperationalCeiling = flightState.activeAircraft?.operationalCeiling
          this.suggestAltitude(flightState.activeAircraft)
        }

        if (this.currentCruiseAltitude !== flightState.activeCruiseAltitude) {
          this.currentCruiseAltitude = flightState.activeCruiseAltitude
        }
      }
    })

    return Result.ok()
  }

  stop(): void | Promise<void> | Result<void> | Promise<Result<void>> {
    if (this.changeRouteSubject) {
      this.changeRouteSubject.unsubscribe()
      this.changeRouteSubject = null
    }

    if (this.changeFlightSubject) {
      this.changeFlightSubject.unsubscribe()
      this.changeFlightSubject = null
    }

    this.restartByRouteSubject = this.routeState.getState().subscribe({
      next: (routeState) => {
        if (!routeState.activeRoute) {
          this.currentRouteId = null
          this.currentDeparture = null
          this.currentDestination = null
          this.settingsState.setCruiseAltitudeSuggesterStatus('running')
        } else if (
          this.currentDeparture?.id?.toString() ===
            routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]?.id?.toString() &&
          this.currentDestination?.id?.toString() === routeState.activeRoute?.waypoints[0]?.id?.toString()
        ) {
          this.currentRouteId = routeState.activeRoute?.id?.toString()
          this.currentDeparture = routeState.activeRoute?.waypoints[0]
          this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          this.settingsState.setCruiseAltitudeSuggesterStatus('running')
        } else if (this.currentRouteId?.toString() !== routeState.activeRoute?.id?.toString()) {
          this.currentRouteId = routeState.activeRoute?.id?.toString()
          this.currentDeparture = routeState.activeRoute?.waypoints[0]
          this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          this.settingsState.setCruiseAltitudeSuggesterStatus('running')
        } else {
          // verificando a mudança nos atributos da classe e atualizando, se necessário

          // currentRouteId
          if (this.currentRouteId?.toString() !== routeState.activeRoute?.id?.toString()) {
            this.currentRouteId = routeState.activeRoute?.id.toString()
          }
          // currentDeparture
          if (this.currentDeparture?.id?.toString() !== routeState.activeRoute?.waypoints[0]?.id?.toString()) {
            this.currentDeparture = routeState.activeRoute?.waypoints[0]
          }
          // currentDestination
          if (
            this.currentDestination?.id?.toString() !==
            routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]?.id?.toString()
          ) {
            this.currentDestination = routeState.activeRoute?.waypoints[routeState.activeRoute.waypoints.length - 1]
          }
        }
      }
    })

    this.restartByFlightSubject = this.flightState.getState().subscribe({
      next: (flightState) => {
        if (this.currentAircraftId?.toString() !== flightState.activeAircraft?.id?.toString()) {
          this.currentAircraftId = flightState.activeAircraft?.id?.toString()
          this.currentOperationalCeiling = flightState.activeAircraft?.operationalCeiling
          this.settingsState.setCruiseAltitudeSuggesterStatus('running')
        } else if (this.currentOperationalCeiling !== flightState.activeAircraft?.operationalCeiling) {
          this.currentOperationalCeiling = flightState.activeAircraft?.operationalCeiling
          this.settingsState.setCruiseAltitudeSuggesterStatus('running')
        } else {
          // verificando a mudança nos atributos da classe e atualizando, se necessário

          // currentAircraftId
          if (this.currentAircraftId?.toString() !== flightState.activeAircraft?.id?.toString()) {
            this.currentAircraftId = flightState.activeAircraft?.id.toString()
          }
          // currentOperationalCeiling
          if (this.currentOperationalCeiling !== flightState.activeAircraft?.operationalCeiling) {
            this.currentOperationalCeiling = flightState.activeAircraft?.operationalCeiling
          }
          // currentCruiseAltitude
          if (this.currentCruiseAltitude !== flightState.activeCruiseAltitude) {
            this.currentCruiseAltitude = flightState.activeCruiseAltitude
          }
        }
      }
    })

    return Result.ok()
  }

  // Método disparar a sugestão de altitude
  private suggestAltitude(mostRecentAircraft?: Aircraft, mostRecentRoute?: Route): Result<void> {
    const route = mostRecentRoute ?? this.routeState?.getStateSnapshot()?.activeRoute
    if (!route) {
      return Result.fail('CruiseAltitudeSuggester: Route state not found')
    }
    const routeWaypoints = route?.waypoints
    if (!routeWaypoints || routeWaypoints.length < 2) {
      return Result.fail('CruiseAltitudeSuggester: Route must have at least 2 waypoints')
    }
    const activeAircraft = mostRecentAircraft ?? this.flightState?.getStateSnapshot()?.activeAircraft
    if (!activeAircraft) {
      return Result.fail('CruiseAltitudeSuggester: Active aircraft not found')
    }

    const routeDistance = route?.totalDistance
    const currentOperationalCeiling = activeAircraft?.operationalCeiling

    const routeDirection = this.routeNavigationState
      .getStateSnapshot()
      .userToWaypointInfo?.get(routeWaypoints[routeWaypoints.length - 1]?.id)?.routeDirection

    const departureElevation = routeWaypoints[0].coordinates.altitude
    const destinationElevation = routeWaypoints[routeWaypoints.length - 1].coordinates.altitude

    const maxAltitude = calculateMaximumCruiseAltitude(
      departureElevation,
      destinationElevation,
      activeAircraft,
      routeDistance
    )
    const minAltitude = calculateMinimumCruiseAltitude(departureElevation, destinationElevation)

    const infosSimulated = simulateWaypointsInfo(
      routeWaypoints[0].coordinates.altitude,
      routeWaypoints[routeWaypoints.length - 1].coordinates.altitude,
      activeAircraft,
      minAltitude,
      routeDistance
    )

    if (infosSimulated?.distanceToTod <= infosSimulated?.distanceToToc) {
      return Result.fail('CruiseAltitudeSuggester: Invalid cruise altitude')
    }

    if (minAltitude > maxAltitude) {
      this.logger.error(
        'CruiseAltitudeSuggester',
        `Minimum cruise altitude (${minAltitude}) is greater than maximum cruise altitude (${maxAltitude}).`
      )
      this.flightState.updateState({
        activeCruiseAltitude: null,
        isCruiseAltitudeSuggested: false
      })
      return
    }

    let suggestedAltitude = this.calculateAltitudeSuggestive(
      routeDistance,
      currentOperationalCeiling,
      routeDirection,
      departureElevation,
      destinationElevation
    )

    if (suggestedAltitude < minAltitude) {
      suggestedAltitude = Math.floor((minAltitude + 500) / 500) * 500
    }
    if (suggestedAltitude > maxAltitude) {
      suggestedAltitude = maxAltitude
    }

    if (this.currentCruiseAltitude !== suggestedAltitude) {
      this.currentCruiseAltitude = suggestedAltitude
      this.flightState.updateState({
        activeCruiseAltitude: suggestedAltitude,
        isCruiseAltitudeSuggested: true
      })
    }
    return Result.ok()
  }

  private calculateAltitudeSuggestive(
    routeDistance: number,
    currentOperationalCeiling: number,
    routeDirection: number,
    departureElevation: number,
    destinationElevation: number
  ): number {
    let minPercent: number
    let maxPercent: number
    let minimalAltitude: number
    let suggestedAltitude: number
    let finalAltitude: number
    let minAltitude: number
    let maxAltitude: number

    // Se as elevações de partida e destino existirem, escolhe a maior entre elas e 500 pés
    if (isSet(departureElevation) && isSet(destinationElevation)) {
      minimalAltitude = Math.max(departureElevation, destinationElevation)
      if (minimalAltitude < 500) {
        minimalAltitude = 500
      }
    } else {
      minimalAltitude = 500
    }

    // Definição dos percentuais mínimos e máximos para cada faixa de teto operacional, de acordo com a distância

    // Teto operacional baixo (até 15.000 pés)
    if (currentOperationalCeiling <= 15000) {
      if (routeDistance <= 50) {
        minPercent = 25
        maxPercent = 35
      } else if (routeDistance <= 100) {
        minPercent = 40
        maxPercent = 50
      } else if (routeDistance <= 200) {
        minPercent = 55
        maxPercent = 65
      } else if (routeDistance <= 400) {
        minPercent = 65
        maxPercent = 70
      } else {
        minPercent = 80
        maxPercent = 100
      }
    }

    // Teto operacional médio (de 15.000 a 30.000 pés)
    else if (currentOperationalCeiling <= 30000) {
      if (routeDistance <= 50) {
        minPercent = 15
        maxPercent = 25
      } else if (routeDistance <= 100) {
        minPercent = 35
        maxPercent = 45
      } else if (routeDistance <= 200) {
        minPercent = 55
        maxPercent = 65
      } else if (routeDistance <= 500) {
        minPercent = 75
        maxPercent = 85
      } else {
        minPercent = 85
        maxPercent = 95
      }
    }

    // Teto operacional alto (acima de 30.000 pés)
    else {
      if (routeDistance <= 50) {
        minPercent = 10
        maxPercent = 20
      } else if (routeDistance <= 100) {
        minPercent = 25
        maxPercent = 35
      } else if (routeDistance <= 200) {
        minPercent = 45
        maxPercent = 55
      } else if (routeDistance <= 500) {
        minPercent = 65
        maxPercent = 75
      } else {
        minPercent = 80
        maxPercent = 90
      }
    }

    // Obtenção de uma altitude sugerida parcial, fazendo a média entre a altitude mínima e máxima definidas pelos percentuais obtidos
    minAltitude = currentOperationalCeiling * (minPercent / 100)
    maxAltitude = currentOperationalCeiling * (maxPercent / 100)
    suggestedAltitude = (minAltitude + maxAltitude) / 2

    // Se a altitude sugerida for menor que a maior elevação entre partida e destino, ajusta para a maior elevação
    if (suggestedAltitude < minimalAltitude) {
      suggestedAltitude = minimalAltitude
    }

    // Limita a altitude sugerida a 10.500 pés para teto operacional até 15.000 pés (teto baixo)
    if (currentOperationalCeiling <= 15000 && suggestedAltitude > 10500) {
      suggestedAltitude = 10500
    }

    let base = Math.trunc(suggestedAltitude / 1000) * 1000

    // VFR: < 14.500 pés; Todos os valores devem ser arredondados para 500 pés
    if (suggestedAltitude < 14500) {
      // Define a altitude final baseada na direção da rota

      // Se a direção da rota estiver entre 0 e 180 graus, a direção é leste e a altitude deve ser ímpar
      if (routeDirection >= 0 && routeDirection < 180) {
        if (base % 2000 !== 0) {
          finalAltitude = base + 500
        } else {
          finalAltitude = base - 500
        }
        // Se a direção da rota estiver entre 180 e 360 graus, a direção é oeste e a altitude deve ser par
      } else {
        if (base % 2000 === 0) {
          finalAltitude = base + 500
        } else {
          finalAltitude = base - 500
        }
      }
    }

    // IFR: 15.000 a 29.000 pés; 2000 pés de separação
    else if (suggestedAltitude < 29000) {
      if (routeDirection >= 0 && routeDirection < 180) {
        if (base % 2000 !== 0) {
          finalAltitude = base
        } else {
          finalAltitude = base - 1000
        }
      } else {
        if (base % 2000 === 0) {
          finalAltitude = base
        } else {
          finalAltitude = base - 1000
        }
      }
    }

    // RVSM: 29.000 a 41.000 pés; 1000 pés de separação
    else if (suggestedAltitude <= 41000) {
      if (routeDirection >= 0 && routeDirection < 180) {
        if (base % 2000 !== 0) {
          finalAltitude = base
        } else {
          finalAltitude = base - 1000
        }
      } else {
        if (base % 2000 === 0) {
          finalAltitude = base
        } else {
          finalAltitude = base - 1000
        }
      }
    }

    // Acima de 41.000 pés; 2000 pés de separação
    else {
      // Lista todas as altitudes possíveis entre 41.000 e o teto operacional
      const possibleAltitudes: number[] = []
      for (let i = 42000; i <= currentOperationalCeiling; i += 2000) {
        possibleAltitudes.push(i)
      }

      // Separa as altitudes possíveis em duas listas, uma para as ímpares e outra para as pares
      let evenAltitudes: number[] = []
      let oddAltitudes: number[] = []
      for (let i = 0; i < possibleAltitudes.length; i++) {
        // levando em conta que 41000 é ímpar e a lista de possíveis altitudes começa em 41000, a primeira possibilidade é ímpar. Daí em diante, as ímpares e pares se alternam
        if (i % 2 === 0) {
          oddAltitudes.push(possibleAltitudes[i])
        } else {
          evenAltitudes.push(possibleAltitudes[i])
        }
      }

      // dependendo da direção da rota, escolhe a altitude mais próxima da sugerida entre as ímpares ou pares
      if (routeDirection >= 0 && routeDirection < 180) {
        // Se a direção da rota estiver entre 0 e 180 graus, a direção é leste e a altitude deve ser ímpar
        finalAltitude = oddAltitudes.reduce((prev, curr) => {
          return Math.abs(curr - suggestedAltitude) < Math.abs(prev - suggestedAltitude) ? curr : prev
        })
      } else {
        // Se a direção da rota estiver entre 180 e 360 graus, a direção é oeste e a altitude deve ser par
        finalAltitude = evenAltitudes.reduce((prev, curr) => {
          return Math.abs(curr - suggestedAltitude) < Math.abs(prev - suggestedAltitude) ? curr : prev
        })
      }
    }

    // Limita a altitude a 10.500 pés para teto operacional até 15.000 pés (teto baixo)
    if (currentOperationalCeiling <= 15000 && finalAltitude > 10500) {
      finalAltitude = 10500
    }

    return finalAltitude
  }
}
